import { Controller } from "@hotwired/stimulus"
import { pluralize, metric, dynos } from "../../lib/helpers"
import { reactor } from "../../lib/reactor"

export default class extends Controller {
  static targets = ["instructionsFrame"]

  static values = {
    type: String,
    language: { type: String, default: "ruby" },
    rubyFramework: { type: String, default: "rails" },
    pythonFramework: { type: String, default: "django_wsgi" },
    nodeFramework: { type: String, default: "express" },
    // Class tokens from AutoscalersHelper (instruction_card_*_classes).
    cardRuntimeActiveClass: String,
    cardFrameworkActiveClass: String,
    cardInactiveClass: String,
    name: { type: String, default: "" },
    // Collection worker library (wire key). Bound from the adapter select.
    adapter: { type: String, default: "" },
    // Integration Turbo Frame compose endpoint (path only).
    instructionsUrl: { type: String, default: "" },
    minimum: Number,
    maximum: Number,
    thresholdDown: Number,
    thresholdUp: Number,
    quantityDown: Number,
    quantityUp: Number,
    sensitivityDown: Number,
    sensitivityUp: Number,
    cooldownDown: Number,
    cooldownUp: Number,
    checkupFrequency: { type: Number, default: 15 },
    // AddOn cooldowns are seconds. Next Root manager timeouts are minutes.
    cooldownUnit: { type: String, default: "seconds" },
    ratio: Number,
    limitDown: Number,
    limitUp: Number,
    aggregation: String,
    percentile: Number,
    lastMinutes: Number,
  }

  // Field keys a threshold/sensitivity preset button may declare via data-*-param.
  static PRESET_FIELDS = [
    "minimum",
    "thresholdDown",
    "thresholdUp",
    "quantityDown",
    "quantityUp",
    "sensitivityDown",
    "sensitivityUp",
    "cooldownDown",
    "cooldownUp",
  ]

  // Match JOB_ADAPTER_LABELS in AutoscalersHelper (wire key → display name).
  static ADAPTER_LABELS = {
    sidekiq: "Sidekiq",
    solid_queue: "Solid Queue",
    good_job: "Good Job",
    que: "Que",
    queue_classic: "Queue Classic",
    delayed_job: "Delayed Job",
    resque: "Resque",
    bunny: "Bunny",
    celery: "Celery",
    rq: "RQ",
    dramatiq: "Dramatiq",
    bullmq: "BullMQ",
    bull: "Bull",
    pg_boss: "pg-boss",
  }

  // Match Instructions::Catalog::ADAPTER_LANGUAGE.
  static ADAPTER_LANGUAGE = {
    sidekiq: "ruby",
    solid_queue: "ruby",
    good_job: "ruby",
    que: "ruby",
    queue_classic: "ruby",
    delayed_job: "ruby",
    resque: "ruby",
    bunny: "ruby",
    celery: "python",
    rq: "python",
    dramatiq: "python",
    bullmq: "node",
    bull: "node",
    pg_boss: "node",
  }

  static DEFAULT_FRAMEWORK = {
    ruby: "rails",
    python: "django_wsgi",
    node: "express",
  }

  // Adapters that reject empty queues (JobPlanConfigurable::QUEUES_REQUIRED_ADAPTERS).
  static QUEUES_REQUIRED_ADAPTERS = new Set(["bunny", "celery", "dramatiq"])

  // Working=Yes adapters only (Autoscaler::HoldWhileWorking::SUPPORTED_ADAPTERS).
  // Conditional libraries and Custom configuration hide the Long-running jobs section.
  static HOLD_WHILE_WORKING_ADAPTERS = new Set([
    "sidekiq",
    "solid_queue",
    "delayed_job",
    "que",
    "good_job",
    "queue_classic",
    "rq",
    "bullmq",
    "bull",
    "pg_boss",
  ])

  connect() {
    this.boundSyncPresetHighlight = this.syncPresetHighlight.bind(this)
    this.instructionsReady = false
    reactor(this)
    // Recompute chip highlight whenever any model field input fires (option 2).
    this.element.addEventListener("input", this.boundSyncPresetHighlight)
    requestAnimationFrame(() => {
      this.boundSyncPresetHighlight()
      this.syncProcessTypeDropdown()
      this.syncAdapterDropdown()
      // Avoid adapter/name valueChanged reloads racing the initial frame src.
      this.instructionsReady = true
      this.reloadInstructionsIfStale()
    })
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundSyncPresetHighlight)
  }

  morph() {
    this.syncPresetHighlight()
  }

  // Queues only apply when a worker library is set (Custom configuration uses app code).
  adapterSelected() {
    return Boolean(this.adapterValue)
  }

  // Long-running jobs section: only when the selected adapter emits trustworthy wrk.
  holdWhileWorkingSupported() {
    return this.constructor.HOLD_WHILE_WORKING_ADAPTERS.has(this.adapterValue)
  }

  // Collection queues field help, tailored to the selected worker library (HTML for highlights).
  queuesHelpDescription() {
    const howTo =
      "Type a name and press Enter, Tab, or a comma to add it (spaces are allowed inside a name)."
    const adapter = this.adapterValue || ""
    if (!adapter) return ""

    const name = this.constructor.ADAPTER_LABELS[adapter] || adapter

    if (this.constructor.QUEUES_REQUIRED_ADAPTERS.has(adapter)) {
      return `${name} requires at least one queue name. ${howTo}`
    }

    const emptyHint = `<strong class="font-semibold text-gray-900 dark:text-white">Leave empty to monitor all queues.</strong>`
    return `Queue names this process works on for ${name}. ${howTo} ${emptyHint}`
  }

  selectLanguage({ params: { language }, currentTarget }) {
    this.languageValue = language
    const defaults = this.constructor.DEFAULT_FRAMEWORK
    if (language === "ruby") this.rubyFrameworkValue = defaults.ruby
    if (language === "python") this.pythonFrameworkValue = defaults.python
    if (language === "node") this.nodeFrameworkValue = defaults.node
    if (currentTarget) {
      this.updateCardStyles(
        currentTarget.closest("[data-language-container]"),
        currentTarget,
        "[data-language-tab]",
        this.cardRuntimeActiveClassValue
      )
    }
    this.reloadInstructions()
  }

  selectFramework({ params: { group, value }, currentTarget }) {
    this[`${group}Value`] = value
    // Tab group is rubyFramework / pythonFramework / nodeFramework.
    if (typeof group === "string" && group.endsWith("Framework")) {
      this.languageValue = group.slice(0, -"Framework".length)
    }
    if (currentTarget) {
      this.updateCardStyles(
        currentTarget.closest("[data-fw-container]"),
        currentTarget,
        "[data-fw-tab]",
        this.cardFrameworkActiveClassValue
      )
    }
    this.reloadInstructions()
  }

  adapterValueChanged() {
    this.syncLanguageFromAdapter()
    this.syncAdapterDropdown()
    this.reloadInstructions()
  }

  // Collection Worker Library rich dropdown: set wire key (blank = Custom).
  // Prefer data-adapter-option so empty Custom is not lost if Stimulus omits blank params.
  selectAdapter({ params, currentTarget }) {
    let adapter = ""
    if (currentTarget?.hasAttribute("data-adapter-option")) {
      adapter = currentTarget.getAttribute("data-adapter-option") || ""
    } else if (params.adapter != null) {
      adapter = String(params.adapter)
    }
    this.setModelField("adapter", adapter)
  }

  // Process-type rich dropdown (formation cache). Custom key opens free-text.
  selectProcessType({ currentTarget }) {
    const key = currentTarget?.getAttribute("data-process-type-option") || ""
    if (key === "__custom__") {
      this.setModelField("name", "")
      this.syncProcessTypeDropdown()
      const customInput = this.element.querySelector(
        "[data-autoscaler--form-process-type-custom-input]"
      )
      if (customInput) {
        customInput.value = ""
        customInput.focus()
      }
      return
    }
    this.setModelField("name", key)
    this.syncProcessTypeDropdown()
  }

  customProcessTypeInput({ currentTarget }) {
    const value = currentTarget?.value || ""
    this.setModelField("name", value)
    this.syncProcessTypeDropdown()
  }

  // Closed trigger + option visibility for the process-type rich dropdown.
  syncProcessTypeDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-process-type-group]")
    if (!group) return

    const name = this.nameValue || ""
    const customKey = "__custom__"

    let matched = false
    group.querySelectorAll("[data-process-type-option]").forEach((option) => {
      const key = option.getAttribute("data-process-type-option") || ""
      if (key === customKey) return
      const isActive = key === name
      if (isActive) matched = true
      option.setAttribute("aria-selected", isActive ? "true" : "false")
      option.hidden = isActive
    })

    const customSelected = !matched
    const customOption = group.querySelector(`[data-process-type-option="${customKey}"]`)
    if (customOption) {
      customOption.setAttribute("aria-selected", customSelected ? "true" : "false")
      customOption.hidden = customSelected
    }

    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    let cueKind = "worker"
    if (customSelected) {
      if (labelEl) labelEl.textContent = group.dataset.customLabel || "Custom"
      if (blurbEl) {
        blurbEl.textContent =
          group.dataset.customBlurb ||
          "Type a process name that is not in the formation list yet."
      }
      cueKind =
        customOption?.getAttribute("data-process-type-cue") || "worker"
    } else {
      const option = group.querySelector(
        `[data-process-type-option="${CSS.escape(name)}"]`
      )
      if (option) {
        const label = option.getAttribute("data-rich-dropdown-option-label")
        const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
        if (labelEl && label != null) labelEl.textContent = label
        if (blurbEl && blurb != null) blurbEl.textContent = blurb
        cueKind = option.getAttribute("data-process-type-cue") || "worker"
      }
    }
    this.syncTriggerAccessibleName(group, labelEl?.textContent)
    this.syncProcessTypeTriggerCue(group, cueKind)

    const customWrap = this.element.querySelector(
      "[data-autoscaler--form-process-type-custom]"
    )
    if (customWrap) customWrap.classList.toggle("hidden", !customSelected)

    const customInput = this.element.querySelector(
      "[data-autoscaler--form-process-type-custom-input]"
    )
    if (customInput && customSelected && customInput.value !== name) {
      customInput.value = name
    }
  }

  // Swap the closed-trigger hex cue (Web vs Worker) from active-tone templates
  // (siblings of the rich-dropdown group, under the form controller element).
  syncProcessTypeTriggerCue(group, cueKind) {
    const kind = cueKind === "web" ? "web" : "worker"
    const source = this.element.querySelector(
      `[data-process-type-cue-source="${kind}"] [data-rich-dropdown-cue]`
    )
    const trigger = group.querySelector(".rich-dropdown-trigger")
    const triggerCue = trigger?.querySelector("[data-rich-dropdown-cue]")
    if (!source || !triggerCue) return
    if (triggerCue.getAttribute("data-process-type-cue") === kind) return
    triggerCue.replaceWith(source.cloneNode(true))
  }

  // Refresh the closed trigger (label, blurb, logo). Hide the selected row from the
  // list so it only appears on the trigger (Strategy dropdown pattern).
  syncAdapterDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-adapter-group]")
    if (!group) return

    const key = this.adapterValue || ""

    group.querySelectorAll("[data-adapter-option]").forEach((option) => {
      const isActive = (option.getAttribute("data-adapter-option") || "") === key
      option.setAttribute("aria-selected", isActive ? "true" : "false")
      option.hidden = isActive
    })

    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    const logoEl = group.querySelector("[data-rich-dropdown-trigger-logo]")
    const option = group.querySelector(`[data-adapter-option="${CSS.escape(key)}"]`)

    if (!option) return

    const label = option.getAttribute("data-rich-dropdown-option-label")
    const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
    if (labelEl && label != null) labelEl.textContent = label
    if (blurbEl && blurb != null) blurbEl.textContent = blurb
    this.syncTriggerAccessibleName(group, labelEl?.textContent)

    if (logoEl) {
      const src = option.getAttribute("data-rich-dropdown-option-logo")
      if (src) logoEl.setAttribute("src", src)
      const invert = option.getAttribute("data-rich-dropdown-option-invert") === "true"
      logoEl.classList.toggle("dark:brightness-0", invert)
      logoEl.classList.toggle("dark:invert", invert)
    }
  }

  nameValueChanged() {
    this.syncProcessTypeDropdown()
    this.reloadInstructions()
  }

  syncLanguageFromAdapter() {
    const adapter = this.adapterValue
    if (!adapter) return

    const lang = this.constructor.ADAPTER_LANGUAGE[adapter]
    if (!lang) return

    this.languageValue = lang
    if (adapter === "solid_queue") {
      this.rubyFrameworkValue = "rails"
      return
    }

    const defaults = this.constructor.DEFAULT_FRAMEWORK
    if (lang === "ruby") this.rubyFrameworkValue = defaults.ruby
    if (lang === "python") this.pythonFrameworkValue = defaults.python
    if (lang === "node") this.nodeFrameworkValue = defaults.node
  }

  strategyKey() {
    const t = this.typeValue || ""
    if (t.includes("RequestQueueTime")) return "rqt"
    if (t.includes("JobQueueLatency") || t.includes("JobLatency")) return "jql"
    if (t.includes("JobQueueSize") || t.includes("JobQueue")) return "jqs"
    if (t.includes("CPUActivity")) return "cpu"
    return "rqt"
  }

  // Discriminator for live field copy (AddOn STI + Next Root manager types).
  metricKind() {
    const t = this.typeValue || ""
    if (t.includes("CPUActivity")) return "cpu"
    if (t.includes("JobQueueLatency") || t.includes("JobLatency")) return "jql"
    if (t.includes("JobQueueSize") || t.includes("JobQueue")) return "jqs"
    if (t.includes("Apdex")) return "apdex"
    if (t.includes("ConnectTime")) return "connect"
    if (t.includes("ResponseTime")) return "response"
    if (t.includes("RPM") || t.includes("RequestsPerMinute")) return "rpm"
    if (t.includes("Load")) return "load"
    if (t.includes("QueueTime") || t.includes("RequestQueueTime")) return "rqt"
    return "rqt"
  }

  currentFramework() {
    const lang = this.languageValue
    if (lang === "ruby") return this.rubyFrameworkValue
    if (lang === "python") return this.pythonFrameworkValue
    return this.nodeFrameworkValue
  }

  // Picks before the ready gate leave src on the default adapter.
  reloadInstructionsIfStale() {
    if (!this.hasInstructionsFrameTarget) return
    const src = this.instructionsFrameTarget.getAttribute("src")
    if (!src) return

    const loaded = new URL(src, window.location.origin).searchParams.get("adapter") || ""
    const wanted = this.adapterValue || ""
    if (loaded !== wanted) this.reloadInstructions()
  }

  reloadInstructions() {
    if (!this.instructionsReady) return
    if (!this.hasInstructionsFrameTarget) return
    const base = this.instructionsUrlValue
    if (!base) return

    const url = new URL(base, window.location.origin)
    url.searchParams.set("strategy", this.strategyKey())
    url.searchParams.set("language", this.languageValue)
    url.searchParams.set("framework", this.currentFramework())
    if (this.adapterValue) {
      url.searchParams.set("adapter", this.adapterValue)
    } else {
      url.searchParams.delete("adapter")
    }
    const name = (this.nameValue || "").trim()
    if (name) url.searchParams.set("name", name)
    else url.searchParams.delete("name")

    this.instructionsFrameTarget.src = url.pathname + url.search
  }

  updateCardStyles(container, activeTab, selector, activeClassString) {
    const active = activeClassString.split(/\s+/).filter(Boolean)
    const inactive = this.cardInactiveClassValue.split(/\s+/).filter(Boolean)
    container.querySelectorAll(selector).forEach(tab => {
      if (tab === activeTab) {
        tab.classList.remove(...inactive)
        tab.classList.add(...active)
      } else {
        tab.classList.remove(...active)
        tab.classList.add(...inactive)
      }
    })
  }

  instanceName() {
    const name = this.nameValue.trim()
    return name ? `:${name}` : ":worker"
  }

  instanceNameRaw() {
    const name = this.nameValue.trim()
    return name ? name : "worker"
  }

  minimumDescription() {
    if (this.minimumValue === 0)
      return `Can scale down to ${dynos("0 dynos")}.`

    return `Won't scale below ${dynos(pluralize(this.minimumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  maximumDescription() {
    return `Won't scale above ${dynos(pluralize(this.maximumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  thresholdDownDescription() {
    switch (this.metricKind()) {
      case "rqt":
        return `Scale down when queue time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "cpu":
        return `Scale down when CPU drops below ${metric(`${this.thresholdDownValue}%`)}.`
      case "jql":
        return `Scale down when latency drops below ${metric(pluralize(this.thresholdDownValue, "{{n}} second", "{{n}} seconds"))}.`
      case "response":
        return `Scale down when response time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "connect":
        return `Scale down when connect time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "apdex":
        return `Scale up when Apdex drops below ${metric(this.hundredths(this.thresholdDownValue))}.`
      case "load":
        return `Scale down when load drops below ${metric(this.hundredths(this.thresholdDownValue))}.`
      default:
        return `Scale down when latency drops below ${metric(pluralize(this.thresholdDownValue, "{{n}} second", "{{n}} seconds"))}.`
    }
  }

  thresholdUpDescription() {
    switch (this.metricKind()) {
      case "rqt":
        return `Scale up when queue time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "cpu":
        return `Scale up when CPU exceeds ${metric(`${this.thresholdUpValue}%`)}.`
      case "jql":
        return `Scale up when latency exceeds ${metric(pluralize(this.thresholdUpValue, "{{n}} second", "{{n}} seconds"))}.`
      case "response":
        return `Scale up when response time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "connect":
        return `Scale up when connect time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "apdex":
        return `Scale down when Apdex reaches ${metric(this.hundredths(this.thresholdUpValue))}.`
      case "load":
        return `Scale up when load exceeds ${metric(this.hundredths(this.thresholdUpValue))}.`
      default:
        return `Scale up when latency exceeds ${metric(pluralize(this.thresholdUpValue, "{{n}} second", "{{n}} seconds"))}.`
    }
  }

  quantityDownDescription() {
    return `Remove ${dynos(pluralize(this.quantityDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  quantityUpDescription() {
    return `Add ${dynos(pluralize(this.quantityUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  ratioDescription() {
    if (this.metricKind() === "rpm") {
      if (this.ratioValue === 1)
        return `${dynos("1 dyno")} targets about ${metric("1 request per minute")}.`

      return `${dynos("1 dyno")} targets about ${metric(pluralize(this.ratioValue, "{{n}} request per minute", "{{n}} requests per minute"))}.`
    }

    if (this.ratioValue === 1)
      return `${dynos("1 dyno")} targets about ${metric("1 job")} of backlog. Not the same as concurrency.`

    return `${dynos("1 dyno")} targets about ${metric(pluralize(this.ratioValue, "{{n}} job", "{{n}} jobs"))} of backlog. Not the same as concurrency.`
  }

  showPercentile() {
    return this.aggregationValue === "percentile"
  }

  aggregationDescription() {
    if (this.aggregationValue === "average")
      return `Use the average across samples.`

    return `Use the ${metric(this.ordinal(this.percentileValue))} percentile across samples.`
  }

  percentileDescription() {
    return `Use the ${metric(this.ordinal(this.percentileValue))} percentile.`
  }

  lastMinutesDescription() {
    if (this.lastMinutesValue === 1)
      return `Use the ${metric("1 minute")} load average.`

    return `Use the ${metric(`${this.lastMinutesValue} minute`)} load average.`
  }

  // Soft threshold/sensitivity preset chips (RQT / JQL / CPU). Loads numbers only.
  applyPreset({ params }) {
    if (!params.key || params.key === "custom") return

    this.constructor.PRESET_FIELDS.forEach((field) => {
      const raw = params[field]
      if (raw === undefined || raw === null || raw === "") return
      this.setModelField(field, Number(raw))
    })
    // input events from setModelField already call syncPresetHighlight.
  }

  // Soft ratio starter-estimate chips (RPM / JQS). Loads ratio only.
  applyRatioBand({ params }) {
    if (!params.key || params.key === "custom") return
    if (params.ratio === undefined || params.ratio === null || params.ratio === "") return

    this.setModelField("ratio", Number(params.ratio))
  }

  setModelField(field, value) {
    this[`${field}Value`] = value

    const input = this.element.querySelector(`[data-autoscaler--form-model="${field}"]`)
    if (!input) return

    const next = String(value)
    if (input.value !== next) input.value = next
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Highlight means "current fields exactly match this chip". Else Custom.
  // Rich-dropdown presets: also refresh the closed trigger label/blurb.
  syncPresetHighlight() {
    const presetGroup = this.element.querySelector("[data-autoscaler--form-preset-group]")
    if (presetGroup) {
      const key = this.matchedPresetKey(presetGroup, "data-autoscaler--form-preset-key")
      this.highlightChoiceGroupByKey(presetGroup, "[data-autoscaler--form-preset-key]", key)
      this.syncRichDropdownTrigger(presetGroup, key, "data-autoscaler--form-preset-key")
      return
    }

    const bandGroup = this.element.querySelector("[data-autoscaler--form-ratio-band-group]")
    if (bandGroup) {
      const key = this.matchedRatioBandKey(bandGroup)
      this.highlightChoiceGroupByKey(bandGroup, "[data-autoscaler--form-ratio-band-key]", key)
      this.syncRichDropdownTrigger(bandGroup, key, "data-autoscaler--form-ratio-band-key")
    }
  }

  // Copy matched option (or Custom) copy into the rich-dropdown trigger spans
  // and keep the trigger accessible name in sync (WCAG 2.5.3 Label in Name).
  syncRichDropdownTrigger(group, activeKey, keyAttribute) {
    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    if (!labelEl || !blurbEl) return

    if (activeKey === "custom") {
      labelEl.textContent = group.dataset.customLabel || "Custom"
      blurbEl.textContent =
        group.dataset.customBlurb ||
        "Your settings do not match a preset. Pick one here or keep editing."
      this.syncTriggerAccessibleName(group, labelEl.textContent)
      return
    }

    const option = group.querySelector(`[${keyAttribute}="${activeKey}"]`)
    if (!option) return
    const label = option.getAttribute("data-rich-dropdown-option-label")
    const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
    if (label != null) labelEl.textContent = label
    if (blurb != null) blurbEl.textContent = blurb
    this.syncTriggerAccessibleName(group, labelEl.textContent)
  }

  // aria-label is "prefix: visible label". Prefix is stamped on the trigger.
  syncTriggerAccessibleName(group, label) {
    const trigger = group.querySelector("[data-popup-target='button']")
    if (!trigger || label == null) return
    const prefix = trigger.getAttribute("data-rich-dropdown-name-prefix")
    trigger.setAttribute("aria-label", prefix ? `${prefix}: ${label}` : label)
  }

  matchedPresetKey(group, keyAttribute) {
    const chips = group.querySelectorAll(`[${keyAttribute}]`)
    for (const chip of chips) {
      const key = chip.getAttribute(keyAttribute)
      if (!key || key === "custom") continue
      if (this.chipMatchesCurrentFields(chip)) return key
    }
    return "custom"
  }

  matchedRatioBandKey(group) {
    const chips = group.querySelectorAll("[data-autoscaler--form-ratio-band-key]")
    for (const chip of chips) {
      const key = chip.getAttribute("data-autoscaler--form-ratio-band-key")
      if (!key || key === "custom") continue
      const ratio = chip.getAttribute("data-autoscaler--form-ratio-param")
      if (ratio === null) continue
      if (Number(ratio) === Number(this.ratioValue)) return key
    }
    return "custom"
  }

  chipMatchesCurrentFields(chip) {
    return this.constructor.PRESET_FIELDS.every((field) => {
      const attr = `data-autoscaler--form-${this.dasherize(field)}-param`
      const raw = chip.getAttribute(attr)
      if (raw === null) return true // field not part of this preset
      return Number(raw) === Number(this[`${field}Value`])
    })
  }

  dasherize(camel) {
    return camel.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
  }

  // List rows are never the filled "active" chip: selection is the trigger only
  // (Strategy / process-type / adapter pattern). Hide the matched option from the
  // list so it does not appear twice. Custom has no list row.
  highlightChoiceGroupByKey(group, selector, activeKey) {
    const keyAttribute = selector.includes("ratio-band")
      ? "data-autoscaler--form-ratio-band-key"
      : "data-autoscaler--form-preset-key"

    group.querySelectorAll(selector).forEach((button) => {
      const key = button.getAttribute(keyAttribute)
      if (key === "custom") return

      const isActive = key === activeKey
      button.hidden = isActive

      if (button.getAttribute("role") === "option") {
        button.setAttribute("aria-selected", isActive ? "true" : "false")
      }
    })
  }

  limitDownDescription() {
    if (this.limitDownValue === 0)
      return `No limit.`

    return `Max decrease of ${dynos(pluralize(this.limitDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  limitUpDescription() {
    if (this.limitUpValue === 0)
      return `No limit.`

    return `Max increase of ${dynos(pluralize(this.limitUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  sensitivityDownDescription() {
    const lag = this.sensitivityLagPhrase(this.sensitivityDownValue)
    if (this.sensitivityDownValue === 1)
      return `Scale down ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale down after ${metric(pluralize(this.sensitivityDownValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  sensitivityUpDescription() {
    const lag = this.sensitivityLagPhrase(this.sensitivityUpValue)
    if (this.sensitivityUpValue === 1)
      return `Scale up ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale up after ${metric(pluralize(this.sensitivityUpValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  cooldownDownDescription() {
    if (!this.cooldownDownValue || this.cooldownDownValue === 0)
      return `${metric("No downscale cooldown")}. Scale down again as soon as sensitivity is met.`

    return `After a downscale, wait ${metric(this.formatCooldown(this.cooldownDownValue))} before another downscale.`
  }

  cooldownUpDescription() {
    if (!this.cooldownUpValue || this.cooldownUpValue === 0)
      return `${metric("No upscale cooldown")}. Scale up again as soon as sensitivity is met (can stack while dynos boot).`

    return `After an upscale, wait ${metric(this.formatCooldown(this.cooldownUpValue))} before another upscale so new capacity can boot.`
  }

  formatCooldown(value) {
    const amount = Number(value) || 0
    if (this.cooldownUnitValue === "minutes") return this.formatDuration(amount * 60)
    return this.formatDuration(value)
  }

  hundredths(value) {
    return (Number(value) / 100).toFixed(2)
  }

  ordinal(value) {
    const n = Number(value) || 0
    const mod100 = n % 100
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`
    switch (n % 10) {
      case 1: return `${n}st`
      case 2: return `${n}nd`
      case 3: return `${n}rd`
      default: return `${n}th`
    }
  }

  sensitivityLagPhrase(count) {
    const frequency = this.checkupFrequencyValue
    if (!count || !frequency) return ""

    const seconds = count * frequency
    return ` (~${this.formatDuration(seconds)} at this app's ${frequency}s checkup frequency)`
  }

  formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0))
    if (seconds < 60) return `${seconds}s`

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const rem = seconds % 60
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (rem > 0) parts.push(`${rem}s`)
    return parts.join(" ")
  }
}
